/**
 * 权限管理器
 * 提供权限检查、角色验证等核心功能
 */

import type {
  Permission,
  Role,
  PermissionCheckOptions,
  AuthState
} from './types'

/**
 * 标准化的权限名称格式
 * 所有权限名称应遵循 module:route:action 格式
 * 例如: system:user:view, tenant:list:view, home:dashboard:view
 */
export const PERMISSION_FORMAT_REGEX = /^[a-z][a-z0-9-]+:[a-z][a-z0-9-]+:[a-z][a-z0-9-]+$/

/**
 * 权限管理器类
 */
export class PermissionManager {
  private permissions: Map<string, Permission> = new Map()
  private roles: Map<string, Role> = new Map()
  private userPermissions: Set<string> = new Set()
  private userRoles: Set<string> = new Set()

  /**
   * 初始化权限数据
   */
  initialize(authState: AuthState): void {
    this.userPermissions.clear()
    this.userRoles.clear()

    // 设置用户权限
    authState.permissions.forEach(permission => {
      this.userPermissions.add(permission)
    })

    // 设置用户角色
    authState.roles.forEach(role => {
      this.userRoles.add(role)
    })
  }

  /**
   * 设置权限定义
   */
  setPermissions(permissions: Permission[]): void {
    this.permissions.clear()
    permissions.forEach(permission => {
      this.permissions.set(permission.code, permission)
    })
  }

  /**
   * 设置角色定义
   */
  setRoles(roles: Role[]): void {
    this.roles.clear()
    roles.forEach(role => {
      this.roles.set(role.code, role)
    })
  }

  /**
   * 检查单个权限
   */
  hasPermission(
    permission: string,
    options: Partial<PermissionCheckOptions> = {}
  ): boolean {
    const { strict = false, context = {} } = options

    // 超级管理员拥有所有权限
    if (this.isSuperAdmin()) {
      return true
    }

    // 验证权限格式是否符合标准
    if (!PERMISSION_FORMAT_REGEX.test(permission)) {
      console.warn(`权限格式不符合标准: ${permission}，应遵循 module:route:action 格式`)
      if (strict) {
        return false
      }
    }

    // 直接权限检查
    if (this.userPermissions.has(permission)) {
      return this.checkPermissionContext(permission, context, strict)
    }

    // 通过角色间接权限检查
    return this.hasPermissionThroughRoles(permission, context, strict)
  }

  /**
   * 检查多个权限
   */
  hasPermissions(
    permissions: string[],
    options: Partial<PermissionCheckOptions> = {}
  ): boolean {
    const { mode = 'all' } = options

    if (permissions.length === 0) {
      return true
    }

    if (mode === 'all') {
      return permissions.every(permission => this.hasPermission(permission, options))
    } else {
      return permissions.some(permission => this.hasPermission(permission, options))
    }
  }

  /**
   * 检查角色
   */
  hasRole(role: string): boolean {
    // 超级管理员拥有所有角色
    if (this.isSuperAdmin()) {
      return true
    }

    return this.userRoles.has(role)
  }

  /**
   * 检查多个角色
   */
  hasRoles(roles: string[], mode: 'all' | 'any' = 'all'): boolean {
    if (roles.length === 0) {
      return true
    }

    if (mode === 'all') {
      return roles.every(role => this.hasRole(role))
    } else {
      return roles.some(role => this.hasRole(role))
    }
  }

  /**
   * 检查是否为超级管理员
   */
  isSuperAdmin(): boolean {
    return this.userRoles.has('super_admin') || this.userRoles.has('admin')
  }

  /**
   * 获取用户权限列表
   */
  getUserPermissions(): string[] {
    return Array.from(this.userPermissions)
  }

  /**
   * 获取用户角色列表
   */
  getUserRoles(): string[] {
    return Array.from(this.userRoles)
  }

  /**
   * 获取权限定义
   */
  getPermissionDefinition(code: string): Permission | undefined {
    return this.permissions.get(code)
  }

  /**
   * 获取角色定义
   */
  getRoleDefinition(code: string): Role | undefined {
    return this.roles.get(code)
  }

  /**
   * 清空权限数据
   */
  clear(): void {
    this.userPermissions.clear()
    this.userRoles.clear()
  }

  /**
   * 通过角色检查权限
   */
  private hasPermissionThroughRoles(
    permission: string,
    context: Record<string, any>,
    strict: boolean
  ): boolean {
    for (const roleCode of this.userRoles) {
      const role = this.roles.get(roleCode)
      if (role && role.permissions.includes(permission)) {
        return this.checkPermissionContext(permission, context, strict)
      }
    }
    return false
  }

  /**
   * 检查权限上下文
   */
  private checkPermissionContext(
    permission: string,
    context: Record<string, any>,
    strict: boolean
  ): boolean {
    // 如果没有上下文要求，直接返回true
    if (Object.keys(context).length === 0) {
      return true
    }

    // 获取权限定义
    const permissionDef = this.permissions.get(permission)
    if (!permissionDef) {
      return !strict // 严格模式下，未定义的权限返回false
    }

    // 这里可以根据具体业务需求实现上下文检查逻辑
    // 例如：检查资源所有者、部门权限等

    return true
  }
}

/**
 * 权限装饰器 - 要求特定权限
 */
export function RequirePermission(permission: string | string[], options?: Partial<PermissionCheckOptions>) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value

    descriptor.value = function (...args: any[]) {
      const permissions = Array.isArray(permission) ? permission : [permission]

      if (!permissionManager.hasPermissions(permissions, options)) {
        throw new Error(`权限不足: 需要权限 ${permissions.join(', ')}`)
      }

      return originalMethod.apply(this, args)
    }

    return descriptor
  }
}

/**
 * 角色装饰器 - 要求特定角色
 */
export function RequireRole(role: string | string[], mode: 'all' | 'any' = 'all') {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value

    descriptor.value = function (...args: any[]) {
      const roles = Array.isArray(role) ? role : [role]

      if (!permissionManager.hasRoles(roles, mode)) {
        throw new Error(`角色不足: 需要角色 ${roles.join(', ')}`)
      }

      return originalMethod.apply(this, args)
    }

    return descriptor
  }
}

// 创建全局权限管理器实例
export const permissionManager = new PermissionManager()
